// lib/axiosClient.ts
import axios from 'axios';
import container from './service-container';
import { IdentityService } from './identity.service';

const axiosClient = axios.create();
let requestInterceptorId: number | null = null;
let responseInterceptorId: number | null = null;
let digitalShelfDebugRequestId = 0;

const isDigitalShelfDebugEnabled = () =>
  typeof window !== 'undefined' &&
  (process.env.NODE_ENV !== 'production' ||
    process.env.NEXT_PUBLIC_DIGITAL_SHELF_DEBUG_LOGS === 'true');

const getDigitalShelfPageTab = (url?: string): string | undefined => {
  const source = (url ?? '').toLowerCase();
  if (!source.includes('/api/digital-shelf')) return undefined;

  if (source.includes('global-score-card')) {
    if (source.includes('trend')) return 'Global Score Card / Trend';
    if (source.includes('country-table') || source.includes('country')) {
      return 'Global Score Card / Country';
    }
    if (source.includes('channel')) return 'Global Score Card / Channel';
    if (source.includes('category')) return 'Global Score Card / Category';
    return 'Global Score Card';
  }

  if (source.includes('share-of-search')) {
    if (source.includes('keyword-insights')) {
      return 'Share of Search / Keyword Insights';
    }
    if (source.includes('search-insights')) {
      return 'Share of Search / Search Insights';
    }
    if (source.includes('keyword-options')) {
      return 'Share of Search / Keyword Options';
    }
    return 'Share of Search';
  }

  if (source.includes('banner-presence')) {
    if (source.includes('content-insight')) {
      return 'Banner Presence / Content Insight';
    }
    if (source.includes('gallery-log')) return 'Banner Presence / Gallery Log';
    if (source.includes('overview')) return 'Banner Presence / Overview';
    return 'Banner Presence';
  }

  if (source.includes('product-health')) {
    if (source.includes('online-availability')) {
      return 'Product Health / Online Availability';
    }
    if (source.includes('ratings-reviews')) {
      return 'Product Health / Rating & Reviews';
    }
    if (source.includes('price-competitiveness')) {
      return 'Product Health / Price Competitiveness';
    }
    if (source.includes('content-quality')) {
      return 'Product Health / Content Quality';
    }
    return 'Product Health';
  }

  return 'Digital Shelf';
};

const getDigitalShelfCallCounts = (): Record<string, number> => {
  const root = window as typeof window & {
    __digitalShelfApiCallCounts?: Record<string, number>;
  };
  root.__digitalShelfApiCallCounts ??= {};
  return root.__digitalShelfApiCallCounts;
};

const logDigitalShelfBrowserEvent = (
  phase: 'started' | 'completed' | 'failed',
  payload: Record<string, unknown>
) => {
  const color =
    phase === 'started'
      ? '#2563eb'
      : phase === 'completed'
        ? '#16a34a'
        : '#dc2626';

  console.info(
    `%c[DigitalShelf][browser] API call ${phase}`,
    `color: ${color}; font-weight: 700; font-size: 12px;`,
    {
      phase,
      ...payload,
    }
  );
};

axiosClient.interceptors.request.use((config) => {
  if (!isDigitalShelfDebugEnabled()) return config;

  const pageTab = getDigitalShelfPageTab(config.url);
  if (!pageTab) return config;

  const counts = getDigitalShelfCallCounts();
  const countKey = `${pageTab}:${config.url}`;
  const callCount = (counts[countKey] ?? 0) + 1;
  counts[countKey] = callCount;

  const metadata = {
    ...((config as any).metadata ?? {}),
    digitalShelfDebugRequestId: ++digitalShelfDebugRequestId,
    digitalShelfStartedAt: Date.now(),
    digitalShelfPageTab: pageTab,
    digitalShelfCallCount: callCount,
  };
  (config as any).metadata = metadata;

  logDigitalShelfBrowserEvent('started', {
    pageTab,
    endpoint: config.url,
    method: config.method,
    callCount,
    requestId: metadata.digitalShelfDebugRequestId,
  });

  return config;
});

axiosClient.interceptors.response.use(
  (response) => {
    const metadata = (response.config as any).metadata;
    if (metadata?.digitalShelfPageTab) {
      logDigitalShelfBrowserEvent('completed', {
        pageTab: metadata.digitalShelfPageTab,
        endpoint: response.config.url,
        method: response.config.method,
        status: response.status,
        callCount: metadata.digitalShelfCallCount,
        requestId: metadata.digitalShelfDebugRequestId,
        durationMs: Date.now() - metadata.digitalShelfStartedAt,
      });
    }
    return response;
  },
  (error) => {
    const metadata = (error.config as any)?.metadata;
    if (metadata?.digitalShelfPageTab) {
      logDigitalShelfBrowserEvent('failed', {
        pageTab: metadata.digitalShelfPageTab,
        endpoint: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        callCount: metadata.digitalShelfCallCount,
        requestId: metadata.digitalShelfDebugRequestId,
        durationMs: Date.now() - metadata.digitalShelfStartedAt,
      });
    }
    return Promise.reject(error);
  }
);

let on429Handler: null | (() => void) = null;
export const register429Handler = (cb: () => void) => {
  on429Handler = cb;
};

export const setupAxiosInterceptor = (token: string) => {
  // Prevent duplicate request interceptor
  if (requestInterceptorId !== null) {
    axiosClient.interceptors.request.eject(requestInterceptorId);
  }
  requestInterceptorId = axiosClient.interceptors.request.use(
    (config) => {
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Prevent duplicate response interceptor
  if (responseInterceptorId !== null) {
    axiosClient.interceptors.response.eject(responseInterceptorId);
  }
  responseInterceptorId = axiosClient.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 503) {
        // Redirect user to maintenance page
        if (typeof window !== 'undefined') {
          window.location.href = '/maintenance';
        }
      }
      // The logout call itself 401s on an already-dead session; handling it
      // here would call logout() again and recurse.
      const isLogoutCall = error.config?.url === '/api/auth/logout';

      if (error.response?.status === 401 && !isLogoutCall) {
        // Redirect user to maintenance page
        if (typeof window !== 'undefined') {
          const identityService =
            container.get<IdentityService>('IdentityService');
          identityService.logout(
            `https://metercube.cube.asia/login?error=${error.response.data.error}`
          );
        }
      }
      if (error.response?.status === 429) {
        if (on429Handler) on429Handler();
      }
      return Promise.reject(error);
    }
  );
};

export const ejectAxiosInterceptor = () => {
  if (requestInterceptorId !== null) {
    axiosClient.interceptors.request.eject(requestInterceptorId);
    requestInterceptorId = null;
  }
  if (responseInterceptorId !== null) {
    axiosClient.interceptors.response.eject(responseInterceptorId);
    responseInterceptorId = null;
  }
};

export default axiosClient;
